import React, { useState, useEffect } from "react";
import { Form, Select } from "antd";
import RequestHandler from "util/RequestHandler";

const { Option } = Select;

export const resolveStockMaterialCategoryId = (values) =>
  values.level5Id ||
  values.level4Id ||
  values.level3Id ||
  values.level2Id ||
  values.level1Id ||
  null;

const StockCategoryLevelSelect = ({ form, initialCategoryId }) => {
  const [level1Categories, setLevel1Categories] = useState([]);
  const [level2Categories, setLevel2Categories] = useState([]);
  const [level3Categories, setLevel3Categories] = useState([]);
  const [level4Categories, setLevel4Categories] = useState([]);
  const [level5Categories, setLevel5Categories] = useState([]);

  const [selectedLevel1Id, setSelectedLevel1Id] = useState(null);
  const [selectedLevel2Id, setSelectedLevel2Id] = useState(null);
  const [selectedLevel3Id, setSelectedLevel3Id] = useState(null);
  const [selectedLevel4Id, setSelectedLevel4Id] = useState(null);

  const getCategories = (parentId, level) => {
    const filter =
      level === 1 ? "ParentId eq null" : `ParentId eq ${parentId}`;

    return RequestHandler.get(`/MaterialCategory?$filter=${filter}`).then(
      (response) => {
        if (!response?.value) return [];
        const data = response.value;
        if (level === 1) setLevel1Categories(data);
        if (level === 2) setLevel2Categories(data);
        if (level === 3) setLevel3Categories(data);
        if (level === 4) setLevel4Categories(data);
        if (level === 5) setLevel5Categories(data);
        return data;
      },
    );
  };

  const resetBelowLevel = (fromLevel) => {
    if (fromLevel <= 1) {
      setLevel2Categories([]);
      setLevel3Categories([]);
      setLevel4Categories([]);
      setLevel5Categories([]);
      setSelectedLevel2Id(null);
      setSelectedLevel3Id(null);
      setSelectedLevel4Id(null);
      form.setFieldsValue({
        level2Id: null,
        level3Id: null,
        level4Id: null,
        level5Id: null,
      });
    } else if (fromLevel <= 2) {
      setLevel3Categories([]);
      setLevel4Categories([]);
      setLevel5Categories([]);
      setSelectedLevel3Id(null);
      setSelectedLevel4Id(null);
      form.setFieldsValue({
        level3Id: null,
        level4Id: null,
        level5Id: null,
      });
    } else if (fromLevel <= 3) {
      setLevel4Categories([]);
      setLevel5Categories([]);
      setSelectedLevel4Id(null);
      form.setFieldsValue({ level4Id: null, level5Id: null });
    } else if (fromLevel <= 4) {
      setLevel5Categories([]);
      form.setFieldsValue({ level5Id: null });
    }
  };

  const loadCategoryChain = async (categoryId) => {
    const chain = [];
    let currentId = categoryId;

    while (currentId) {
      const response = await RequestHandler.get(
        `/MaterialCategory?$filter=Id eq ${currentId}`,
      );
      const category = response?.value?.[0];
      if (!category) break;
      chain.unshift(category);
      currentId = category.parentId;
    }

    if (!chain.length) return;

    await getCategories(null, 1);
    form.setFieldsValue({ level1Id: chain[0].id });
    setSelectedLevel1Id(chain[0].id);

    for (let i = 1; i < chain.length; i++) {
      await getCategories(chain[i - 1].id, i + 1);
      form.setFieldsValue({ [`level${i + 1}Id`]: chain[i].id });
      if (i === 1) setSelectedLevel2Id(chain[i].id);
      if (i === 2) setSelectedLevel3Id(chain[i].id);
      if (i === 3) setSelectedLevel4Id(chain[i].id);
    }
  };

  useEffect(() => {
    getCategories(null, 1);
  }, []);

  useEffect(() => {
    if (initialCategoryId) {
      loadCategoryChain(initialCategoryId);
    }
  }, [initialCategoryId]);

  const handleLevel1Change = (value) => {
    setSelectedLevel1Id(value);
    resetBelowLevel(1);
    if (value) getCategories(value, 2);
  };

  const handleLevel2Change = (value) => {
    setSelectedLevel2Id(value);
    resetBelowLevel(2);
    if (value) getCategories(value, 3);
  };

  const handleLevel3Change = (value) => {
    setSelectedLevel3Id(value);
    resetBelowLevel(3);
    if (value) getCategories(value, 4);
  };

  const handleLevel4Change = (value) => {
    setSelectedLevel4Id(value);
    resetBelowLevel(4);
    if (value) getCategories(value, 5);
  };

  return (
    <>
      <Form.Item label="Kategori - Seviye 1" name="level1Id">
        <Select allowClear placeholder="Seviye 1 seçiniz" onChange={handleLevel1Change}>
          {level1Categories.map((cat) => (
            <Option key={cat.id} value={cat.id}>
              {cat.name}
            </Option>
          ))}
        </Select>
      </Form.Item>

      {selectedLevel1Id && (
        <Form.Item label="Kategori - Seviye 2" name="level2Id">
          <Select allowClear placeholder="Seviye 2 seçiniz" onChange={handleLevel2Change}>
            {level2Categories.map((cat) => (
              <Option key={cat.id} value={cat.id}>
                {cat.name}
              </Option>
            ))}
          </Select>
        </Form.Item>
      )}

      {selectedLevel2Id && (
        <Form.Item label="Kategori - Seviye 3" name="level3Id">
          <Select allowClear placeholder="Seviye 3 seçiniz" onChange={handleLevel3Change}>
            {level3Categories.map((cat) => (
              <Option key={cat.id} value={cat.id}>
                {cat.name}
              </Option>
            ))}
          </Select>
        </Form.Item>
      )}

      {selectedLevel3Id && (
        <Form.Item label="Kategori - Seviye 4" name="level4Id">
          <Select allowClear placeholder="Seviye 4 seçiniz" onChange={handleLevel4Change}>
            {level4Categories.map((cat) => (
              <Option key={cat.id} value={cat.id}>
                {cat.name}
              </Option>
            ))}
          </Select>
        </Form.Item>
      )}

      {selectedLevel4Id && (
        <Form.Item label="Kategori - Seviye 5" name="level5Id">
          <Select allowClear placeholder="Seviye 5 seçiniz">
            {level5Categories.map((cat) => (
              <Option key={cat.id} value={cat.id}>
                {cat.name}
              </Option>
            ))}
          </Select>
        </Form.Item>
      )}
    </>
  );
};

export default StockCategoryLevelSelect;
